import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';

export default function EditInventory() {
    const navigate = useNavigate();
    const location = useLocation();
    const inventoryId = location.state?.id;

    const [labData, setLabData] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        quantity: 1,
        lab_id: '', // Change lab_name to lab_id to store the ID
        description: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        fetchLabs();
        if (inventoryId) {
            fetchInventoryData(inventoryId);
        }
    }, [inventoryId]); // Add inventoryId to dependency array if not already there

    const fetchLabs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('https://api.nvcti.in/labs/getAllLabs', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setLabData(response.data.data);
        } catch (error) {
            toast.error('Failed to fetch labs');
        }
    };

    const fetchInventoryData = async (id) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            // Make sure the API endpoint is correct for fetching single inventory item
            const response = await axios.get(`https://api.nvcti.in/api/inventory/getInventoryById/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            // The response.data directly contains the inventory item
            const inventoryItem = response.data;

            setFormData({
                name: inventoryItem.name,
                quantity: inventoryItem.quantity,
                lab_id: inventoryItem.lab_id, // Use lab_id from the fetched inventory
                description: inventoryItem.description
            });

        } catch (error) {
            console.error('Error fetching inventory data:', error);
            toast.error('Failed to fetch inventory data');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        let valid = true;
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Inventory name is required';
            valid = false;
        }

        // if (!formData.quantity || isNaN(formData.quantity) || formData.quantity <= 0) {
        //     newErrors.quantity = 'Please enter a valid quantity';
        //     valid = false;
        // }

        if (!formData.lab_id) { // Check for lab_id here
            newErrors.lab_id = 'Please select a lab'; // Error message should reflect lab_id
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `https://api.nvcti.in/api/inventory/updateInventory/${inventoryId}`,
                // `https://api.nvcti.in/api/inventory/updateInventory/${inventoryId}`,
                // Send lab_id in the payload
                { ...formData, lab_id: formData.lab_id },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 200) {
                toast.success('Inventory updated successfully');
                navigate('/admin/inventory');
            }
        } catch (error) {
            console.error('Error updating inventory:', error);
            toast.error('Failed to update inventory');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toaster position="bottom-left" reverseOrder={false} />
            <div className="flex justify-center items-center">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 mt-6 shadow-lg rounded-lg bg-dark-secondary border-0">
                    <div className="rounded-t bg-dark-tertiary mb-0 px-6 py-6">
                        <div className="text-center flex justify-between">
                            <h6 className="text-white text-xl font-bold">Edit Inventory</h6>
                        </div>
                    </div>
                    <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
                        <form onSubmit={handleSubmit}>
                            <h6 className="text-gray-400 text-1xl mt-3 mb-6 font-bold">Inventory Information</h6>
                            <div className="flex flex-wrap">
                                <div className="w-full lg:w-6/12 px-4">
                                    <div className="relative w-full mb-3">
                                        <label className="block text-gray-300 text-xs font-bold mb-2">Inventory Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                            placeholder="Inventory Name"
                                        />
                                        {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                                    </div>
                                </div>
                                {/* <div className="w-full lg:w-6/12 px-4">
                                    <div className="relative w-full mb-3">
                                        <label className="block text-blueGray-600 text-xs font-bold mb-2">Quantity</label>
                                        <input
                                            type="number"
                                            name="quantity"
                                            value={formData.quantity}
                                            onChange={handleChange}
                                            className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                            placeholder="Quantity"
                                        />
                                        {errors.quantity && <p className="text-red-500 text-xs">{errors.quantity}</p>}
                                    </div>
                                </div> */}
                                <div className="w-full lg:w-6/12 px-4">
                                    <div className="relative w-full mb-3">
                                        <label className="block text-gray-300 text-xs font-bold mb-2">Lab Name</label>
                                        <select
                                            name="lab_id" 
                                            value={formData.lab_id}
                                            onChange={handleChange}
                                            className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        >
                                            <option value="">Select Lab</option> 
                                            {labData.map(lab => (
                                                <option key={lab.lab_id} value={lab.lab_id}>
                                                    {lab.lab_name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.lab_id && <p className="text-red-500 text-xs">{errors.lab_id}</p>}
                                    </div>
                                </div>
                            </div>
                            <hr className="mt-6 border-b-1 border-blueGray-300" />
                            <h6 className="text-gray-400 text-sm mt-3 mb-6 font-bold">Description</h6>
                            <div className="flex flex-wrap">
                                <div className="w-full lg:w-12/12 px-4">
                                    <div className="relative w-full mb-3">
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                            rows="4"
                                            placeholder="Description"
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="flex justify-end w-full">
                                    <Tooltip title="Back" arrow>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/admin/inventory')}
                                            className="mt-10 text-white bg-blueGray-700 active:bg-blueGray-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-auto ease-linear transition-all duration-150"
                                        >
                                            {"<< "}Back
                                        </button>
                                    </Tooltip>
                                    <Tooltip title="Update" arrow>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="bg-lightBlue-500 mt-10 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                                        >
                                            {loading ? 'Updating...' : 'Update'}
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}